import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, MessageCircle, Loader2, Send, Check, AlertTriangle,
  ToggleLeft, ToggleRight, TestTube2, History,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const TRIGGER_META = {
  lesson_reminder_24h: { label: "24-hour lesson reminder", desc: "Sent the day before each booked lesson." },
  running_late: { label: "Running late alert", desc: "Fires when you hit the one-tap 'I'm late' button." },
  payment_confirmation: { label: "Payment confirmation", desc: "Sent after a pupil completes payment." },
  booking_confirmation: { label: "Booking confirmation", desc: "Sent when a lesson is first booked." },
  general_message: { label: "In-app message → SMS", desc: "Allows you to mirror an in-app message as SMS." },
};

export default function SmsSettings() {
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [log, setLog] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testBody, setTestBody] = useState("Test SMS from your Cook's dashboard.");
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/api/sms/settings`, { withCredentials: true }),
        axios.get(`${API}/api/sms/log`, { withCredentials: true }),
      ]);
      // Normalise the payload so a missing field never crashes the
      // render tree (operator truth pass, Feb 27, 2026).
      const normalised = {
        twilio_configured: false,
        enabled: false,
        triggers: {},
        templates: {},
        available_triggers: Object.keys(TRIGGER_META),
        default_templates: {},
        ...(s.data || {}),
      };
      setSettings(normalised);
      setLog(Array.isArray(l.data?.log) ? l.data.log : []);
    } catch {
      // Calm degraded state — render the unconfigured holding card.
      setSettings(null);
      toast.error("Could not load SMS settings");
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/sms/settings`, patch, { withCredentials: true });
      setSettings(data);
    } catch {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const toggleEnabled = () => save({ enabled: !settings.enabled });
  const toggleTrigger = (key) => save({ triggers: { ...(settings.triggers || {}), [key]: !settings.triggers?.[key] } });
  const updateTemplate = (key, value) => setSettings(s => ({ ...s, templates: { ...(s.templates || {}), [key]: value } }));
  const saveTemplates = () => save({ templates: settings.templates || {} });

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a mobile number"); return; }
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/api/sms/test-send`,
        { to: testTo.trim(), body: testBody }, { withCredentials: true });
      if (data.status === "sent") toast.success(`Sent — Twilio sid ${data.sid?.slice(0, 10)}...`);
      else if (data.status === "not_configured") toast.info("SMS stubbed (no Twilio keys yet) — logged for audit.");
      else toast.error(`Status: ${data.status}${data.error ? ` · ${data.error}` : ""}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    }
    setTesting(false);
  };

  if (loading) {
    return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }
  if (!settings) {
    // Backend unreachable OR settings doc malformed. Render a calm
    // not-configured holding state so the user is never trapped in
    // a half-loaded page (Feb 27, 2026 — operator truth pass).
    return (
      <div data-testid="sms-settings-unavailable" className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
        <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
          <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
            <button onClick={() => nav("/instructor")} className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            </button>
            <p className="text-sm font-extrabold text-slate-900 dark:text-white">SMS Settings</p>
          </div>
        </header>
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <h2 className="text-base font-extrabold text-slate-900 dark:text-white">SMS is not configured yet</h2>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-400">
              SMS messaging needs Twilio credentials. We&apos;ll enable
              this page once Twilio is set up. You can keep using the
              platform normally — nothing is broken.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Defensive defaults — backend should always return these, but
  // never let a missing key crash render (operator truth pass).
  const availableTriggers = Array.isArray(settings.available_triggers) && settings.available_triggers.length > 0
    ? settings.available_triggers
    : Object.keys(TRIGGER_META);
  const triggersMap = settings.triggers || {};
  const templatesMap = settings.templates || {};

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="sms-back" onClick={() => nav("/instructor")} className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1">
            <p className="text-sm font-extrabold text-slate-900 dark:text-white">SMS Settings</p>
            <p className="text-[10px] text-slate-500 dark:text-slate-400">Twilio-powered pupil messaging</p>
          </div>
          {!settings.twilio_configured && (
            <span data-testid="sms-not-configured" className="px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-bold flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Keys pending
            </span>
          )}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Master toggle */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="text-lg font-extrabold text-slate-900 dark:text-white">Enable SMS channel</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Master switch. Turn off to silence all outbound SMS.</p>
            </div>
            <button data-testid="sms-master-toggle" onClick={toggleEnabled} disabled={saving} className="text-blue-600">
              {settings.enabled ? <ToggleRight className="w-10 h-10" /> : <ToggleLeft className="w-10 h-10 text-slate-400" />}
            </button>
          </div>
          {!settings.twilio_configured && (
            <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Twilio credentials not yet configured. SMS attempts are logged but not sent. Add <code className="font-mono font-bold">TWILIO_ACCOUNT_SID</code>, <code className="font-mono font-bold">TWILIO_AUTH_TOKEN</code>, and <code className="font-mono font-bold">TWILIO_FROM_NUMBER</code> to activate.</span>
            </div>
          )}
        </div>

        {/* Trigger toggles */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm">
          <h2 className="text-lg font-extrabold text-slate-900 dark:text-white mb-3">Triggers</h2>
          <div className="space-y-2">
            {availableTriggers.map(key => {
              const meta = TRIGGER_META[key] || { label: key, desc: "" };
              const on = triggersMap[key];
              return (
                <div key={key} className="flex items-center justify-between py-2.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900 dark:text-white">{meta.label}</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">{meta.desc}</p>
                  </div>
                  <button data-testid={`sms-trigger-${key}`} onClick={() => toggleTrigger(key)} disabled={saving || !settings.enabled}
                    className={`text-blue-600 disabled:opacity-50`}>
                    {on ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 text-slate-400" />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Templates */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-extrabold text-slate-900 dark:text-white">Templates</h2>
            <Button data-testid="sms-save-templates" onClick={saveTemplates} disabled={saving} size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />Save</>}
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3">Use placeholders like <code className="font-mono">{"{name}"}</code>, <code className="font-mono">{"{time}"}</code>, <code className="font-mono">{"{date}"}</code>, <code className="font-mono">{"{amount}"}</code>, <code className="font-mono">{"{instructor}"}</code>, <code className="font-mono">{"{minutes}"}</code>.</p>
          {availableTriggers.map(key => (
            <div key={key} className="mb-3 last:mb-0">
              <Label className="text-xs">{TRIGGER_META[key]?.label || key}</Label>
              <Textarea data-testid={`sms-template-${key}`} rows={2}
                value={templatesMap[key] || ""}
                onChange={e => updateTemplate(key, e.target.value)}
                className="text-xs mt-1" />
            </div>
          ))}
        </div>

        {/* Test send */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm">
          <h2 className="text-lg font-extrabold text-slate-900 dark:text-white mb-1 flex items-center gap-2"><TestTube2 className="w-4 h-4 text-blue-600" /> Test send</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">Send a one-off test to any UK mobile.</p>
          <div className="space-y-2">
            <Input data-testid="sms-test-to" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="07xxx xxxxxx" />
            <Textarea data-testid="sms-test-body" rows={2} value={testBody} onChange={e => setTestBody(e.target.value)} className="text-xs" />
            <Button data-testid="sms-test-send" onClick={sendTest} disabled={testing || !testTo.trim()}
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send test</>}
            </Button>
          </div>
        </div>

        {/* Log */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm">
          <h2 className="text-lg font-extrabold text-slate-900 dark:text-white mb-3 flex items-center gap-2"><History className="w-4 h-4 text-slate-500" /> Recent SMS</h2>
          {log.length === 0 && <p className="text-xs text-slate-500">No SMS activity yet.</p>}
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {log.map(l => (
              <div key={l.id} data-testid={`sms-log-${l.id}`} className="flex items-start gap-2 text-[11px] bg-slate-50 dark:bg-slate-900 rounded-lg px-2.5 py-1.5">
                <StatusBadge status={l.status} />
                <div className="flex-1 min-w-0">
                  <p className="text-slate-700 dark:text-slate-300 truncate">{l.body}</p>
                  <p className="text-slate-400 text-[10px]">
                    {l.trigger} · to {l.to} · {new Date(l.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent: { label: "sent", color: "bg-emerald-100 text-emerald-700" },
    not_configured: { label: "stub", color: "bg-amber-100 text-amber-700" },
    failed: { label: "failed", color: "bg-red-100 text-red-700" },
    invalid_number: { label: "bad#", color: "bg-red-100 text-red-700" },
  };
  const s = map[status] || { label: status, color: "bg-slate-100 text-slate-600" };
  return <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase flex-shrink-0 ${s.color}`}>{s.label}</span>;
}
